// ============================================================
// TakeExam.jsx - نسخه نهایی با صفحه نتیجه زیبا برای کودکان
// و پشتیبانی از جوایز چالش‌ها (سکه و XP)
// ============================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import './TakeExam.css';
import { useCoins } from '../../context/CoinContext.jsx';
import { useExams, toNumber, sanitizeHtml, calculatePassThreshold, idsEqual } from './ExamContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';

import { getImageUrl } from '../../services/ImageService';

// 🔽 CHANGED: curriculum helper برای نمایش رشته
import { getField as getFieldMeta } from '../../core/education/curriculum.js';




const getUserDisplayName = (user) => {
  if (!user) return 'دانش‌آموز';
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`.trim();
  if (user.firstName) return user.firstName;
  if (user.lastName) return user.lastName;
  if (user.name) return user.name;
  if (user.fullName) return user.fullName;
  if (user.username) return user.username;
  return 'دانش‌آموز';
};



// ===== تابع محاسبه امن (برای ماشین حساب) =====
const safeEvaluate = (expression) => {
  const sanitized = expression.replace(/[^0-9+\-*/().]/g, '');
  if (!sanitized) return '';
  
  try {
    const result = Function(`"use strict"; return (${sanitized})`)();
    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
      return result;
    }
    return 'خطا';
  } catch {
    try {
      const parts = sanitized.match(/(\d+\.?\d*)\s*([+\-*/])\s*(\d+\.?\d*)/);
      if (parts) {
        const a = parseFloat(parts[1]);
        const op = parts[2];
        const b = parseFloat(parts[3]);
        switch (op) {
          case '+': return a + b;
          case '-': return a - b;
          case '*': return a * b;
          case '/': return b !== 0 ? a / b : 'خطا';
          default: return 'خطا';
        }
      }
      return 'خطا';
    } catch {
      return 'خطا';
    }
  }
};



// 🔽 CHANGED: helper برای نمایش «پایه دهم (ریاضی)» — فقط اگه رشته وجود داشته باشه
const formatGradeField = (grade, field) => {
  if (!grade) return '';
  if (!field) return grade;
  const fieldMeta = getFieldMeta(field);
  const fieldText = fieldMeta?.shortTitle || fieldMeta?.title || '';
  return fieldText ? `${grade} (${fieldText})` : grade;
};



// ===== تابع تبدیل اعداد انگلیسی به فارسی =====
const toPersianDigits = (num) => {
  if (num === undefined || num === null) return '';
  const id = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return num.toString().replace(/[0-9]/g, (w) => id[+w]);
};

// ===== تابع تبدیل حروف انگلیسی به فارسی =====
const toPersianLetter = (letter) => {
  const map = {
    'A': 'الف', 'B': 'ب', 'C': 'پ', 'D': 'ت',
    'E': 'ث', 'F': 'ج', 'G': 'چ', 'H': 'ح',
    'I': 'خ', 'J': 'د', 'K': 'ذ', 'L': 'ر',
    'M': 'ز', 'N': 'ژ', 'O': 'س', 'P': 'ش',
    'Q': 'ص', 'R': 'ض', 'S': 'ط', 'T': 'ظ',
    'U': 'ع', 'V': 'غ', 'W': 'ف', 'X': 'ق',
    'Y': 'ک', 'Z': 'گ'
  };
  return map[letter] || letter;
};

const TakeExam = ({
  examId: propExamId = null,
  onSubmitOverride = null,   // 🆕
  passThreshold = null,       // 🆕
  embeddedMode = false,       // 🆕
  hideNavigation = false,     // 🆕
}) => {
  const params = useParams();
  const navigate = useNavigate();   // 🆕 ← این خط را اضافه کن
  const examId = propExamId || params.examId;
  
  const { 
    exams, 
    updateExamResults, 
    getExam, 
    canStudentAttempt, 
    hasStudentParticipated, 
    isExamOpenForEntry,
    getStudentAttempts,
  } = useExams();

  // ===== دریافت اطلاعات کاربر از AuthContext =====
  const { user } = useAuth();
  const currentStudentId = user?.id || null;
  const studentName = getUserDisplayName(user) || 'دانش‌آموز';//const studentName = user?.name || 'دانش‌آموز';

  const examData = useMemo(() => {
    if (!examId) return null;
    return getExam(examId) || exams.find((e) => e.id === examId);
  }, [examId, exams, getExam]);

  // ============================================================
  // ===== Stateها (قبل از useEffectها) =====
  // ============================================================
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [bookmarked, setBookmarked] = useState([]);
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedTime = localStorage.getItem(`exam_${examData?.id || ''}_time`);
    if (savedTime) {
      return parseInt(savedTime, 10);
    }
    return (toNumber(examData?.duration) || 0) * 60;
  });
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [isPaused, setIsPaused] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorInput, setCalculatorInput] = useState('');
  const [calculatorResult, setCalculatorResult] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [missingRequired, setMissingRequired] = useState([]);
  const [examStarted, setExamStarted] = useState(false);
  const [showResultModal, setShowResultModal] = useState(true);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [navFilter, setNavFilter] = useState('all');
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);

  // ===== استفاده از CoinContext برای مدیریت سکه و XP =====
  const { addCoins, addXp, addRewards } = useCoins(); // ← addRewards را اضافه کنید








// ===== هماهنگ‌سازی تم با ناوبر =====
const [isDark, setIsDark] = useState(() => {
  try { return localStorage.getItem('navbar2-drawer-dark') === '1'; }
  catch { return false; }
});

useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') setIsDark(e.newValue === '1');
  };
  window.addEventListener('storage', handleStorage);
  const interval = setInterval(() => {
    try {
      const cur = localStorage.getItem('navbar2-drawer-dark') === '1';
      setIsDark(prev => (prev !== cur ? cur : prev));
    } catch {}
  }, 500);
  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);






  const timerRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // ============================================================
  // ===== چک کردن مجوز ورود به آزمون (با در نظر گرفتن isSubmitted) =====
  // ============================================================
  useEffect(() => {
    if (isSubmitted) return;
    if (!examData || !currentStudentId) return;

    if (!isExamOpenForEntry(examData)) {
      const now = new Date();
      const start = examData.startTime ? new Date(examData.startTime) : null;
      const end = examData.endTime ? new Date(examData.endTime) : null;

      let message = '⏳ زمان ورود به این آزمون به پایان رسیده است.';
      if (start && !isNaN(start.getTime()) && now < start) {
        const timeStr = start.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
        const dateStr = start.toLocaleDateString('fa-IR');
        message = `⏳ این آزمون از ساعت ${timeStr} تاریخ ${dateStr} شروع می‌شود.`;
      } else if (end && !isNaN(end.getTime()) && now > end) {
        message = '⏳ زمان ورود به این آزمون به پایان رسیده است.';
      }

      alert(message);
      navigate('/exams/student');
      return;
    }

    if (hasStudentParticipated(examId, currentStudentId)) {
      const limit = examData.limitAttempts || 1;
      if (examData.isGlobal && limit > 1) {
        const attempts = getStudentAttempts(examId, currentStudentId);
        if (attempts >= limit) {
          alert(`⚠️ شما قبلاً ${limit} بار در این آزمون شرکت کرده‌اید و دیگر مجاز به شرکت نیستید.`);
          navigate('/exams/student');
          return;
        }
      } else {
        alert('⚠️ شما قبلاً در این آزمون شرکت کرده‌اید و امکان شرکت مجدد وجود ندارد.');
        navigate('/exams/student');
        return;
      }
    }

    if (!canStudentAttempt(examId, currentStudentId)) {
      alert(`⚠️ شما بیش از ${examData.limitAttempts || 1} بار مجاز به شرکت در این آزمون نیستید.`);
      navigate('/exams/student');
      return;
    }

    if (examData.statusType === 'closed') {
      alert('🔒 این آزمون بسته شده است و امکان شرکت وجود ندارد.');
      navigate('/exams/student');
      return;
    }

  }, [examData, examId, currentStudentId, navigate, isExamOpenForEntry, hasStudentParticipated, canStudentAttempt, getStudentAttempts, isSubmitted]);

  const isGlobal = examData?.isGlobal || false;
  const previousAttempts = currentStudentId 
    ? (examData?.results?.filter(r => idsEqual(r.studentId, currentStudentId))?.length || 0) 
    : 0;

  // ===== مدیریت ماشین حساب =====
  const handleCalculator = (value) => {
    if (value === '=') {
      const result = safeEvaluate(calculatorInput);
      setCalculatorResult(result);
    } else if (value === 'C') {
      setCalculatorInput('');
      setCalculatorResult('');
    } else {
      setCalculatorInput((prev) => prev + value);
    }
  };

  // ===== Shuffle =====
  useEffect(() => {
    if (!examData?.questions) return;

    let questions = [...examData.questions];
    if (examData.shuffleQuestions) {
      questions = shuffleArray(questions);
    }
    if (examData.shuffleOptions) {
      questions = questions.map((q) => {
        if (q.type === 'multiple' || q.type === 'multi') {
          const shuffled = shuffleArray(
            q.options.map((opt, idx) => ({ opt, idx }))
          );
          const newOptions = shuffled.map((item) => item.opt);
          
          let newCorrect;
          if (q.type === 'multiple') {
            newCorrect = shuffled.findIndex((item) => item.idx === q.correctAnswer);
          } else {
            const correctIndices = q.correctAnswers || [];
            newCorrect = shuffled
              .filter((item) => correctIndices.includes(item.idx))
              .map((item) => shuffled.indexOf(item));
          }
          
          return {
            ...q,
            options: newOptions,
            correctAnswer: newCorrect,
            correctAnswers: newCorrect,
          };
        }
        return q;
      });
    }
    setShuffledQuestions(questions);
  }, [examData]);

  // ===== تایمر =====
  useEffect(() => {
    if (timeLeft > 0 && !isSubmitted && !isPaused && examStarted) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeLeft, isSubmitted, isPaused, examStarted]);

  // ===== توقف موقت =====
  const togglePause = () => {
    setIsPaused(!isPaused);
    if (!isPaused) {
      localStorage.setItem(`exam_${examData.id}_time`, timeLeft);
    }
  };





  // ===== هشدار خروج از آزمون (back، بستن مرورگر، رفرش) =====
useEffect(() => {
  if (!examStarted || isSubmitted) return;

  // 1️⃣ جلوگیری از بستن/رفرش مرورگر
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    e.returnValue = 'اگر از صفحه خارج شوید، آزمون شما پایان می‌یابد!';
    return e.returnValue;
  };
  window.addEventListener('beforeunload', handleBeforeUnload);

  // 2️⃣ جلوگیری از دکمه back مرورگر
  window.history.pushState(null, '', window.location.href);

  const handlePopState = () => {
    // دوباره push می‌کنیم تا کاربر واقعاً back نرود
    window.history.pushState(null, '', window.location.href);
    // نمایش مودال هشدار
    setShowLeaveWarning(true);
  };
  window.addEventListener('popstate', handlePopState);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('popstate', handlePopState);
  };
}, [examStarted, isSubmitted]);






  // ===== ذخیره خودکار =====
  useEffect(() => {
    if (!examData) return;
    if (Object.keys(answers).length > 0 && !isSubmitted && examStarted) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        localStorage.setItem(`exam_${examData.id}_answers`, JSON.stringify(answers));
        localStorage.setItem(`exam_${examData.id}_time`, timeLeft);
        localStorage.setItem(`exam_${examData.id}_step`, currentStep);
      }, 2000);
    }
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [answers, timeLeft, currentStep, examData?.id, isSubmitted, examStarted]);

  // ===== بازیابی =====
  useEffect(() => {
    if (!examData) return;
    if (!examStarted) return;
    const saved = localStorage.getItem(`exam_${examData.id}_answers`);
    if (saved) {
      try { setAnswers(JSON.parse(saved)); } catch { /* ignore */ }
    }
    const savedTime = localStorage.getItem(`exam_${examData.id}_time`);
    if (savedTime) setTimeLeft(parseInt(savedTime, 10));
    const savedStep = localStorage.getItem(`exam_${examData.id}_step`);
    if (savedStep) setCurrentStep(parseInt(savedStep, 10));
  }, [examData?.id, examStarted]);

  // ===== توابع کمکی =====
  const shuffleArray = (arr) => {
    const newArr = [...arr];
    for (let i = newArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isAnswered = (index) => {
    const q = shuffledQuestions[index];
    if (!q) return false;
    const answer = answers[index];

    switch (q.type) {
      case 'multiple':
      case 'truefalse':
        return answer !== undefined && answer !== null && answer !== -1;
      case 'multi':
        return answer && answer.length > 0;
      case 'fill':
        return answer && Array.isArray(answer) && answer.some((a) => a?.trim() !== '');
      case 'essay':
      case 'file':
        return answer && answer.trim() !== '';
      case 'matching':
        if (!answer || typeof answer !== 'object') return false;
        const pairs = q.matchingPairs || [];
        return pairs.every((_, i) => answer[i] !== undefined && answer[i] !== '');
      case 'ordering':
        return answer && answer.length === q.orderItems?.length;
      default:
        return false;
    }
  };

  const getAnsweredCount = () => {
    let count = 0;
    for (let i = 0; i < shuffledQuestions.length; i++) {
      if (isAnswered(i)) count++;
    }
    return count;
  };

  const getMissingRequiredQuestions = () => {
    const missing = [];
    shuffledQuestions.forEach((q, idx) => {
      if (q.required && !isAnswered(idx)) missing.push(idx);
    });
    return missing;
  };

  // ===== شروع آزمون =====
  const startExam = () => {
    if (!examData) return;
    setExamStarted(true);
    const saved = localStorage.getItem(`exam_${examData.id}_answers`);
    if (saved) {
      try { setAnswers(JSON.parse(saved)); } catch { /* ignore */ }
    }
  };

  // ===== Handlers =====
  const handleAnswer = (index, value) => {
    setAnswers(prev => ({ ...prev, [index]: value }));
    const missing = getMissingRequiredQuestions();
    setMissingRequired(missing);
  };

  const handleMultiAnswer = (index, optionIdx) => {
    const current = answers[index] || [];
    const newAnswers = current.includes(optionIdx)
      ? current.filter(i => i !== optionIdx)
      : [...current, optionIdx];
    setAnswers(prev => ({ ...prev, [index]: newAnswers }));
    const missing = getMissingRequiredQuestions();
    setMissingRequired(missing);
  };

  const handleFillAnswer = (index, fillIdx, value) => {
    const current = answers[index] || [];
    const newFill = [...current];
    newFill[fillIdx] = value;
    setAnswers(prev => ({ ...prev, [index]: newFill }));
    const missing = getMissingRequiredQuestions();
    setMissingRequired(missing);
  };

  const handleFileUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('حجم فایل نباید بیشتر از ۱۰ مگابایت باشد');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAnswers(prev => ({ ...prev, [index]: reader.result }));
        const missing = getMissingRequiredQuestions();
        setMissingRequired(missing);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMatchingAnswer = (index, pairIdx, value) => {
    const current = answers[index] || {};
    setAnswers(prev => ({
      ...prev,
      [index]: { ...current, [pairIdx]: value },
    }));
    const missing = getMissingRequiredQuestions();
    setMissingRequired(missing);
  };

  // ===== مدیریت مرتب‌سازی (Ordering) با انتخاب ترتیبی =====
  const handleOrderingSelect = (index, itemIdx) => {
    const q = shuffledQuestions[index];
    if (!q) return;

    const currentOrder = answers[index] || q.orderItems.map((_, i) => i);
    
    const existingPos = currentOrder.indexOf(itemIdx);
    if (existingPos !== -1) {
      const newOrder = currentOrder.filter((_, i) => i !== existingPos);
      setAnswers(prev => ({ ...prev, [index]: newOrder }));
      const missing = getMissingRequiredQuestions();
      setMissingRequired(missing);
      return;
    }

    const newOrder = [...currentOrder, itemIdx];
    setAnswers(prev => ({ ...prev, [index]: newOrder }));
    const missing = getMissingRequiredQuestions();
    setMissingRequired(missing);
  };

  const isOrderItemSelected = (index, itemIdx) => {
    const q = shuffledQuestions[index];
    if (!q) return false;
    const currentOrder = answers[index] || q.orderItems.map((_, i) => i);
    return currentOrder.includes(itemIdx);
  };

  const getOrderItemPosition = (index, itemIdx) => {
    const q = shuffledQuestions[index];
    if (!q) return -1;
    const currentOrder = answers[index] || q.orderItems.map((_, i) => i);
    const pos = currentOrder.indexOf(itemIdx);
    return pos !== -1 ? pos + 1 : -1;
  };

  const toggleBookmark = (index) => {
    setBookmarked(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < shuffledQuestions.length) {
      setCurrentStep(index);
    }
  };

  const nextQuestion = () => {
    if (currentStep < shuffledQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevQuestion = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const missing = getMissingRequiredQuestions();
    if (missing.length > 0) {
      setMissingRequired(missing);
      alert(`⚠️ لطفاً به سوالات اجباری شماره ${missing.map(idx => idx + 1).join('، ')} پاسخ دهید.`);
      setCurrentStep(missing[0]);
      return;
    }
    setShowConfirm(true);
  };

  // ============================================================
  // ===== محاسبه نتیجه =====
  // ============================================================
  const calculateResult = useCallback(() => {
    let totalScore = 0;
    let maxScore = 0;
    let essayCount = 0;
    const details = [];

    shuffledQuestions.forEach((q, idx) => {
      const isEssay = q.type === 'essay' || q.type === 'file';
      const qScore = toNumber(q.score, 1);
      maxScore += qScore;
      const answer = answers[idx];
      let isCorrect = false;
      let userAnswer = answer;

      if (isEssay) {
        isCorrect = null;
        essayCount++;
      } else if (q.type === 'multiple' || q.type === 'truefalse') {
        isCorrect = answer === q.correctAnswer;
        if (isCorrect) totalScore += qScore;
      } else if (q.type === 'multi') {
        const correct = q.correctAnswers || [];
        const user = Array.isArray(answer) ? answer : [];
        isCorrect = correct.length === user.length && correct.every(c => user.includes(c));
        if (isCorrect) totalScore += qScore;
      } else if (q.type === 'fill') {
        const user = Array.isArray(answer) ? answer : [];
        const correct = q.fillAnswers || [];
        isCorrect = user.length === correct.length && user.every((u, i) => u?.trim().toLowerCase() === correct[i]?.trim().toLowerCase());
        if (isCorrect) totalScore += qScore;
      } else if (q.type === 'matching') {
        const user = typeof answer === 'object' && answer !== null ? answer : {};
        const pairs = q.matchingPairs || [];
        isCorrect = pairs.every((_, i) => user[i] === i);
        if (isCorrect) totalScore += qScore;
      } else if (q.type === 'ordering') {
        const user = Array.isArray(answer) ? answer : [];
        isCorrect = user.every((val, i) => val === i);
        if (isCorrect) totalScore += qScore;
      }

      details.push({
        questionIndex: idx,
        question: q.question,
        type: q.type,
        userAnswer: userAnswer,
        isCorrect,
        score: isCorrect === true ? qScore : 0,
        maxScore: qScore,
        isEssay,
        questionText: q.question,
        answerText: isEssay ? (answer || 'پاسخی ثبت نشده') : null,
      });
    });

    const threshold = passThreshold ?? calculatePassThreshold(maxScore);
return {
  totalScore,
  maxScore,
  details,
  percentage: maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0,
  isPassed: maxScore > 0 ? totalScore >= threshold : false,
  passThreshold: threshold,   // 🆕
  hasEssay: essayCount > 0,
  essayCount,
};
  }, [shuffledQuestions, answers, passThreshold]);

  const buildFullAnswers = useCallback((resultData) => {
    return shuffledQuestions.map((q, idx) => {
      const answer = answers[idx];
      const isEssay = q.type === 'essay' || q.type === 'file';

      if (isEssay) {
        return {
          questionId: q.id,
          questionIndex: idx,
          text: answer || '',
          isCorrect: null,
          type: q.type,
        };
      }

      const detail = resultData.details.find((d) => d.questionIndex === idx);
      return {
        questionId: q.id,
        questionIndex: idx,
        text: typeof answer === 'string' ? answer : JSON.stringify(answer),
        isCorrect: detail?.isCorrect || false,
        type: q.type,
      };
    });
  }, [shuffledQuestions, answers]);

  // ============================================================
  // ===== ارسال آزمون =====
  // ============================================================
const submitExam = useCallback(async () => {
  if (!examData) return;
  if (isSubmitting) return;
  
  setIsSubmitting(true);
  setShowConfirm(false);
  setIsSubmitted(true);
  if (timerRef.current) clearInterval(timerRef.current);

  const resultData = calculateResult();
  
  const freshExamData = getExam(examData.id) || examData;

  // ============================================================
  // 🆕 جوایز چالش — در embedded mode اجرا نکن
  // ============================================================
  if (!embeddedMode && freshExamData.isChallenge && currentStudentId) {
    const score = resultData.totalScore;
    let coins = 0, xp = 0;

    if (score === 20) {
      coins = toNumber(freshExamData.coinsExcellent, 0);
      xp = toNumber(freshExamData.xpExcellent, 0);
    } else if (score >= 16) {
      coins = toNumber(freshExamData.coinsVeryGood, 0);
      xp = toNumber(freshExamData.xpVeryGood, 0);
    } else if (score >= 12) {
      coins = toNumber(freshExamData.coinsGood, 0);
      xp = toNumber(freshExamData.xpGood, 0);
    } else if (score >= 8) {
      coins = toNumber(freshExamData.coinsNeedsWork, 0);
      xp = toNumber(freshExamData.xpNeedsWork, 0);
    } else if (score >= 4) {
      coins = toNumber(freshExamData.coinsUnacceptable, 0);
      xp = toNumber(freshExamData.xpUnacceptable, 0);
    } else {
      coins = toNumber(freshExamData.coinsFailed, 0);
      xp = toNumber(freshExamData.xpFailed, 0);
    }

    if (coins > 0 || xp > 0) {
      addRewards(coins, xp);
    }

    if (coins > 0 || xp > 0) {
      setTimeout(() => {
        alert(`🎉 تبریک! ${coins} سکه و ${xp} امتیاز به حساب شما اضافه شد.`);
      }, 1000);
    }
  }

  // ============================================================
  // مهمان — قبل از پاک‌سازی بپرس
  // ============================================================
  if (!currentStudentId && !embeddedMode) {
    const msg = 'شما وارد حساب خود نشده‌اید. آیا می‌خواهید وارد شوید تا نتایج ذخیره شود؟';
    const shouldLogin = typeof window.askConfirm === 'function'
      ? await window.askConfirm(msg)
      : window.confirm(msg);
    if (shouldLogin) {
      navigate('/login');
      setIsSubmitting(false);
      return;
    }
  }

  // پاک‌سازی localStorage — فقط بعد از تصمیم کاربر
  localStorage.removeItem(`exam_${examData.id}_answers`);
  localStorage.removeItem(`exam_${examData.id}_time`);
  localStorage.removeItem(`exam_${examData.id}_step`);

  // ============================================================
  // ذخیره‌ی نتیجه در ExamContext (همیشه، شامل step quiz)
  // ============================================================
  const studentId = currentStudentId || `guest_${Date.now().toString(36)}`;
  const fullAnswers = buildFullAnswers(resultData);
  const hasEssay = shuffledQuestions.some((q) => q.type === 'essay' || q.type === 'file');
  const actualScore = resultData.details
    .filter((d) => d.isCorrect !== null)
    .reduce((sum, d) => sum + (d.isCorrect ? d.score : 0), 0);

  const examResult = {
    studentId,
    score: actualScore,
    answers: fullAnswers,
    isEssayGraded: !hasEssay,
    details: resultData.details,
    submittedAt: new Date().toISOString(),
    studentName: currentStudentId ? studentName : 'میهمان',
    attemptNumber: previousAttempts + 1,
    isFinalSubmit: true,
  };

  updateExamResults(examData.id, examResult);

  // ============================================================
  // 🆕 Step Quiz Mode: صدا زدن onSubmitOverride قبل از هر چیز دیگر
  // ============================================================
  if (onSubmitOverride) {
    try {
      await onSubmitOverride({
        totalScore: resultData.totalScore,
        maxScore: resultData.maxScore,
        percentage: resultData.percentage,
        isPassed: resultData.isPassed,
        passThreshold: resultData.passThreshold,
        details: resultData.details,
        hasEssay: resultData.hasEssay,
      });
    } catch (e) {
      console.error('[TakeExam] onSubmitOverride failed:', e);
    }

    setIsSubmitting(false);

    // در embedded mode، خود TakeExam نمایش نتیجه و ناوبری نکنه
    // (StepQuizRunner خودش view رو به result تغییر می‌ده)
    if (embeddedMode) return;
  }

  // ============================================================
  // حالت عادی: نمایش کارنامه (بدون navigate فوری)
  // ============================================================
  setResult(resultData);
  setShowResultModal(true);

  // ⚠️ navigate حذف شد — چون باعث unmount شدن کامپوننت قبل از
  // نمایش کارنامه می‌شد. navigate حالا در closeResult انجام می‌شود.
}, [
  examData?.id,
  currentStudentId,
  calculateResult,
  buildFullAnswers,
  shuffledQuestions,
  updateExamResults,
  isSubmitting,
  studentName,
  navigate,
  previousAttempts,
  examData?.isChallenge,
  examData?.coinsExcellent,
  examData?.xpExcellent,
  examData?.coinsVeryGood,
  examData?.xpVeryGood,
  examData?.coinsGood,
  examData?.xpGood,
  addRewards,          // 🆕 ← این خط اضافه بشه
  examData?.coinsNeedsWork,
  examData?.xpNeedsWork,
  examData?.coinsUnacceptable,
  examData?.xpUnacceptable,
  examData?.coinsFailed,
  examData?.xpFailed,
  getExam,
  // 🆕
  onSubmitOverride,
  embeddedMode,
]);

  const handleAutoSubmit = useCallback(() => {
    if (!isSubmitted) submitExam();
  }, [isSubmitted, submitExam]);

  const confirmSubmit = useCallback(() => {
    submitExam();
  }, [submitExam]);

const closeResult = () => {
  setShowResultModal(false);
  if (embeddedMode) return;

  // 🆕 تعیین مقصد بر اساس نوع آزمون
  if (examData?.isChallenge) {
    navigate('/challenge-arena');
  } else {
    navigate('/exams/student');
  }
};

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullScreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullScreen(false);
    }
  };

  // ===== فیلتر سوالات =====
  const filteredQuestions = useMemo(() => {
    return shuffledQuestions.map((q, idx) => ({
      ...q,
      index: idx,
      isAnswered: isAnswered(idx),
      isBookmarked: bookmarked.includes(idx),
    }));
  }, [shuffledQuestions, bookmarked]);

  // ===== تابع تعیین سطح عملکرد =====
  const getPerformanceLevel = (score) => {
    if (score >= 20) return { label: '🌟 عالی', emoji: '🌟', color: '#fbbf24', description: 'شگفت‌انگیز! شما واقعاً عالی بودید!' };
    if (score >= 16) return { label: '🎉 خیلی خوب', emoji: '🎉', color: '#34d399', description: 'کارت عالی بود! ادامه بده!' };
    if (score >= 12) return { label: '👍 خوب', emoji: '👍', color: '#60a5fa', description: 'خوب بود! می‌تونی بهتر بشی!' };
    if (score >= 8) return { label: '💪 نیاز به تلاش بیشتر', emoji: '💪', color: '#f59e0b', description: 'تمرین کن تا بهتر بشی!' };
    if (score >= 4) return { label: '📖 غیر قابل قبول', emoji: '📖', color: '#f472b6', description: 'بیشتر مطالعه کن!' };
    return { label: '🔄 مردود', emoji: '🔄', color: '#ef4444', description: 'نگران نباش، دوباره تلاش کن!' };
  };

  // ============================================================
  // ===== رندر سوال =====
  // ============================================================
  const renderQuestion = (q, index) => {
    const answer = answers[index];
    const isBookmarked = bookmarked.includes(index);
    const questionStyle = { fontSize: `${fontSize}px` };
    const isRequired = q.required && !isAnswered(index);
    const isEssay = q.type === 'essay' || q.type === 'file';

    return (
      <div className="take-exam questionContainer">
        <div className="take-exam questionHeader">
          <div className="take-exam questionMeta">
            <span className="take-exam questionNumber">
               سوال {toPersianDigits(index + 1)} از {toPersianDigits(shuffledQuestions.length)}
            </span>
            {q.score && <span className="take-exam questionScore">نمره: {toNumber(q.score, 1)}</span>}
            {q.timeLimit && <span className="take-exam timeLimitBadge">⏱ {q.timeLimit}ث</span>}
            {q.required && (
              <span className={`take-exam requiredBadge ${isRequired ? 'missingBadge' : ''}`}>
                {isRequired ? '⚠️ الزامی' : 'الزامی'}
              </span>
            )}
            {isEssay && <span className="take-exam essayBadge">📝 تشریحی</span>}
            <button
              className={`take-exam bookmarkBtn ${isBookmarked ? 'active' : ''}`}
              onClick={() => toggleBookmark(index)}
            >
              {isBookmarked ? '⭐' : '☆'}
            </button>
          </div>
          <div className="take-exam statusIndicator">
            {isAnswered(index) ? (
              <span className="take-exam answered">✓ پاسخ داده شد</span>
            ) : (
              <span className="take-exam unanswered">• پاسخ داده نشد</span>
            )}
          </div>
        </div>

        <div
          className="take-exam questionText"
          style={questionStyle}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(q.question) }}
        />

        {q.image && (
  <div className="take-exam questionImage">
    <img src={getImageUrl(q.image)} alt={`تصویر سوال ${index + 1}`} />
  </div>
)}

        {q.mathFormula && (
          <div className="take-exam mathDisplay">
            <span className="take-exam mathLabel">📐 فرمول: </span>
            <span className="take-exam mathFormulaDisplay">{q.mathFormula}</span>
          </div>
        )}

        <div className="take-exam answerArea">
          {q.type === 'multiple' && (
            <div className="take-exam optionsList">
              {q.options.map((opt, oi) => {
                const isSelected = answer !== undefined && answer !== null && answer === oi;
                return (
                  <label
                    key={oi}
                    className={`take-exam optionLabel ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`q_${index}`}
                      checked={Boolean(isSelected)}
                      onChange={() => handleAnswer(index, oi)}
                    />
                    <span className="take-exam optionText">
                      <span className="take-exam optionLetter">
                        {toPersianLetter(String.fromCharCode(65 + oi))}.
                      </span>
                      {opt}
                    </span>
                    {isSelected && <span className="take-exam checkMark">✓</span>}
                  </label>
                );
              })}
            </div>
          )}

          {q.type === 'multi' && (
            <div className="take-exam optionsList">
              {q.options.map((opt, oi) => {
                const isSelected = (answer || []).includes(oi);
                return (
                  <label
                    key={oi}
                    className={`take-exam optionLabel ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(isSelected)}
                      onChange={() => handleMultiAnswer(index, oi)}
                    />
                    <span className="take-exam optionText">
                      <span className="take-exam optionLetter">
                        {toPersianLetter(String.fromCharCode(65 + oi))}.
                      </span>
                      {opt}
                    </span>
                    {isSelected && <span className="take-exam checkMark">✓</span>}
                  </label>
                );
              })}
            </div>
          )}

          {q.type === 'truefalse' && (
            <div className="take-exam optionsList">
              {[
                { label: 'صحیح', value: 0 },
                { label: 'غلط', value: 1 }
              ].map((item) => {
                const isSelected = answer !== undefined && answer !== null && answer === item.value;
                return (
                  <label
                    key={item.value}
                    className={`take-exam optionLabel ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`q_${index}`}
                      checked={Boolean(isSelected)}
                      onChange={() => handleAnswer(index, item.value)}
                    />
                    <span className="take-exam optionText">{item.label}</span>
                    {isSelected && <span className="take-exam checkMark">✓</span>}
                  </label>
                );
              })}
            </div>
          )}

          {q.type === 'fill' && (
            <div className="take-exam fillAnswers">
              {q.fillAnswers.map((_, fi) => (
                <div key={fi} className="take-exam fillRow">
                  <span className="take-exam fillLabel">جای خالی {fi + 1}:</span>
                  <input
                    type="text"
                    className="take-exam fillInput"
                    value={(answer || [])[fi] || ''}
                    onChange={(e) => handleFillAnswer(index, fi, e.target.value)}
                    placeholder="پاسخ را وارد کنید..."
                  />
                </div>
              ))}
            </div>
          )}

          {q.type === 'essay' && (
            <div className="take-exam essayArea">
              <textarea
                className="take-exam essayInput"
                value={answer || ''}
                onChange={(e) => handleAnswer(index, e.target.value)}
                placeholder="پاسخ خود را در اینجا بنویسید..."
                rows="6"
              />
            </div>
          )}

          {q.type === 'file' && (
            <div className="take-exam fileArea">
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx,.mp3,.mp4"
                onChange={(e) => handleFileUpload(index, e)}
                className="take-exam fileInput"
              />
              {answer && answer.startsWith('data:image') && (
                <div className="take-exam uploadedImage">
                  <img src={answer} alt="فایل آپلود شده" />
                </div>
              )}
              {answer && !answer.startsWith('data:image') && (
                <div className="take-exam uploadedFile">
                  <span>📎 فایل آپلود شد</span>
                  <button
                    className="take-exam removeFileBtn"
                    onClick={() => handleAnswer(index, '')}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}

          {q.type === 'matching' && (
            <div className="take-exam matchingArea">
              {q.matchingPairs.map((pair, pi) => (
                <div key={pi} className="take-exam matchingRow">
                  <span className="take-exam matchingLeft">{pair.left}</span>
                  <span className="take-exam matchingArrow">⟷</span>
                  <select
                    className="take-exam matchingSelect"
                    value={(answer || {})[pi] !== undefined ? (answer || {})[pi] : ''}
                    onChange={(e) =>
                      handleMatchingAnswer(index, pi, parseInt(e.target.value, 10))
                    }
                  >
                    <option value="">-- انتخاب --</option>
                    {q.matchingPairs.map((_, si) => (
                      <option key={si} value={si}>
                        {q.matchingPairs[si]?.right || `گزینه ${si + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {q.type === 'ordering' && (
            <div className="take-exam orderingArea">
              <p className="take-exam orderingInstruction">
                برای مرتب‌سازی، روی گزینه‌ها به ترتیب دلخواه کلیک کنید.
                <br />
                <span className="take-exam orderingHint">
                  (هر بار کلیک، گزینه را به انتهای لیست اضافه یا از آن حذف می‌کند)
                </span>
              </p>
              <div className="take-exam orderingItems">
                {q.orderItems.map((item, oi) => {
                  const isSelected = isOrderItemSelected(index, oi);
                  const position = getOrderItemPosition(index, oi);
                  return (
                    <div
                      key={oi}
                      className={`take-exam orderingItem ${isSelected ? 'ordered' : ''}`}
                      onClick={() => handleOrderingSelect(index, oi)}
                    >
                      <span className="take-exam orderingNumber">
                        {isSelected ? position : '☐'}
                      </span>
                      <span className="take-exam orderingText">{item}</span>
                      {isSelected && <span className="take-exam orderingRemove">✕</span>}
                    </div>
                  );
                })}
              </div>
              <div className="take-exam orderingStatus">
                <span>
                  {answers[index]?.length || 0} از {q.orderItems.length} آیتم انتخاب شد
                </span>
                {answers[index]?.length === q.orderItems.length && (
                  <span className="take-exam orderingComplete">✅ مرتب‌سازی کامل شد</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ===== نمایش همه سوالات در یک صفحه =====
  const renderAllQuestions = () => {
    return shuffledQuestions.map((q, idx) => (
      <div key={idx} className="take-exam allQuestionsContainer">
        {renderQuestion(q, idx)}
        {idx < shuffledQuestions.length - 1 && (
          <div className="take-exam questionDivider">━━━━━━━━━━━━━━━━━━</div>
        )}
      </div>
    ));
  };

  // ============================================================
  // ===== رندر صفحه شروع =====
  // ============================================================
  const renderStartScreen = () => {
    if (examStarted) return null;

    // 🔽 کمک‌کننده: آیکن درس (جایگزین 📝 ثابت)
    const getSubjectEmoji = (subject) => {
      const map = {
        'ریاضی': '🔬', 'فیزیک': '⚡', 'شیمی': '🧪', 'زیست': '🧬',
        'فارسی': '📜', 'عربی': '🌙', 'زبان': '🌍', 'دینی': '📖',
        'قرآن': '📿', 'علوم': '🔭', 'مطالعات': '🗺️', 'هدیه': '🎁',
      };
      return map[subject] || '📚';
    };

    const subjectIcon = getSubjectEmoji(examData.subject);
    const hasImage = !!examData.image;
    const questionCount = examData.questions?.length || 0;
    const duration = examData.duration || 0;
    const maxAttempts = examData.limitAttempts || 1;

    return (
      <div className="take-exam startScreen">
        <div className="take-exam startCard">
          <button className="take-exam startBackBtn" onClick={() => window.history.back()} aria-label="بازگشت">
            ✕
          </button>

          {isGlobal && (
            <span className="take-exam startRibbon">🌍 عمومی</span>
          )}

          {/* ===== Hero: تصویر/گرادیان + عنوان ===== */}
          <div className="take-exam startHero">
            <div className="take-exam startHeroMedia">
              <div className="take-exam startHeroFallback">
                <span className="take-exam startHeroFallbackIcon">{subjectIcon}</span>
              </div>
              {hasImage && (
                <img
                  src={getImageUrl(examData.image)}
                  alt="تصویر آزمون"
                  className="take-exam startHeroImage"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </div>

            <div className="take-exam startHeroContent">
              <h1 className={`take-exam startTitle ${hasImage ? 'has-badge' : ''}`}>
                {examData.title}
              </h1>

              <div className="take-exam startChips">
                {examData.book && (
                  <span className="take-exam startChip">📚 {examData.book}</span>
                )}
                {isGlobal && examData.grade && (
                  <span className="take-exam startChip">
                    🎓 {formatGradeField(examData.grade, examData.field)}
                  </span>
                )}
                {isGlobal && examData.subject && (
                  <span className="take-exam startChip">✏️ {examData.subject}</span>
                )}
                {isGlobal && examData.chapter && (
                  <span className="take-exam startChip">📖 {examData.chapter}</span>
                )}
              </div>
            </div>
          </div>

          {/* ===== Stats Bar ===== */}
          <div className="take-exam startStatsBar">
            <div className="take-exam startStat">
              <span className="take-exam startStatIcon">📝</span>
              <span className="take-exam startStatValue">{questionCount}</span>
              <span className="take-exam startStatLabel">سوال</span>
            </div>
            <div className="take-exam startStat">
              <span className="take-exam startStatIcon">⏱</span>
              <span className="take-exam startStatValue">{duration}</span>
              <span className="take-exam startStatLabel">دقیقه</span>
            </div>
            {isGlobal && (
              <div className="take-exam startStat">
                <span className="take-exam startStatIcon">🔄</span>
                <span className="take-exam startStatValue">{maxAttempts}</span>
                <span className="take-exam startStatLabel">بار مجاز</span>
              </div>
            )}
            {previousAttempts > 0 && isGlobal && (
              <div className="take-exam startStat">
                <span className="take-exam startStatIcon">📊</span>
                <span className="take-exam startStatValue">{previousAttempts}</span>
                <span className="take-exam startStatLabel">تلاش قبلی</span>
              </div>
            )}
          </div>

          {/* ===== توضیحات ===== */}
          {examData.description && (
            <div className="take-exam startDescription">
              <p>{examData.description}</p>
            </div>
          )}

          {/* ===== دکمه شروع ===== */}
          <button className="take-exam startBtn" onClick={startExam}>
            🚀 شروع آزمون
          </button>
        </div>
      </div>
    );
  };













  // ============================================================
  // ===== نمایش نتیجه (نسخه جدید و زیبا برای کودکان) =====
  // ============================================================
  useEffect(() => {
    if (isSubmitted && result && showResultModal) {
      const isExcellent = result.totalScore >= 16;
      if (isExcellent) {
        setTimeout(() => {
          const count = 3;
          for (let i = 0; i < count; i++) {
            setTimeout(() => {
              confetti({
                particleCount: 80,
                spread: 70,
                origin: { y: 0.6 },
                colors: ['#fbbf24', '#f472b6', '#60a5fa', '#34d399', '#a78bfa']
              });
            }, i * 500);
          }
        }, 500);
      }
    }
  }, [isSubmitted, result, showResultModal]);




  // ✅ حالا که همه‌ی هوک‌ها بدون قید و شرط اجرا شده‌اند، قانون Hooks رعایت شده
  // و می‌توانیم early return را در جای درست قرار دهیم.
  if (!examData) {
    return <div className={`take-exam error ${isDark ? 'dark-mode' : ''}`}>آزمون یافت نشد</div>;
  }

 



  if (isSubmitted && result && showResultModal) {
    const hasEssay = result.hasEssay || false;
    const isFullyGraded = result.details.every((d) => d.isCorrect !== null);
    const canReattempt = isGlobal && (previousAttempts < (examData.limitAttempts || 1) - 1);
    const maxAttempts = examData.limitAttempts || 1;
    
    const performance = getPerformanceLevel(result.totalScore);
    const isExcellent = result.totalScore >= 16;

    return (
      <div className={`take-exam resultContainer ${isDark ? 'dark-mode' : ''}`}>
        <div className="take-exam resultCard">
          <button className="take-exam resultCloseBtn" onClick={closeResult} title="بستن کارنامه">
            ✕
          </button>

          <div className="take-exam resultHeader">
            <div className="take-exam resultHeaderContent">
              <span className="take-exam resultEmoji">{performance.emoji}</span>
              <h2 className="take-exam resultTitle">کارنامه آزمون</h2>
            </div>
            <span className="take-exam resultExamTitle">{examData.title}</span>
            {isGlobal && <span className="take-exam resultGlobalBadge">🌍 عمومی</span>}
            {/* 🔽 CHANGED: نمایش رشته در کارنامه */}
            {isGlobal && examData.grade && (
              <span className="take-exam resultGradeField">
                {formatGradeField(examData.grade, examData.field)}
                {examData.subject ? ` • ${examData.subject}` : ''}
              </span>
            )}
            {hasEssay && !isFullyGraded && (
              <span className="take-exam resultCorrectionBadge">
                ⏳ در انتظار تصحیح
              </span>
            )}
            {isGlobal && (
              <span className="take-exam resultAttemptBadge">
                تلاش {previousAttempts + 1} از {maxAttempts}
              </span>
            )}
          </div>

          <div className="take-exam resultScore">
            <div className="take-exam resultScoreCircle">
              <span className="take-exam resultScoreNumber">
                {hasEssay && !isFullyGraded ? '--' : result.totalScore}
              </span>
            </div>
            <div className="take-exam resultScoreInfo">
              <span className="take-exam resultScoreLabel">نمره شما</span>
              <span className="take-exam resultScoreMax">از {result.maxScore}</span>
            </div>
          </div>

          <div className="take-exam resultPerformance">
            <div className="take-exam resultPerformanceBadge" style={{ background: performance.color }}>
              <span className="take-exam resultPerformanceEmoji">{performance.emoji}</span>
              <span className="take-exam resultPerformanceLabel">{performance.label}</span>
            </div>
            <p className="take-exam resultPerformanceDesc">{performance.description}</p>
          </div>

          <div className="take-exam resultDetails">
            <h4 className="take-exam resultDetailsTitle">📋 جزئیات پاسخ‌ها</h4>
            <div className="take-exam resultDetailsTableWrap">
              <table className="take-exam resultDetailsTable">
                <thead>
                  <tr>
                    <th>سوال</th>
                    <th>نوع</th>
                    <th>وضعیت</th>
                    <th>نمره</th>
                  </tr>
                </thead>
                <tbody>
                  {result.details.map((d, idx) => {
                    const isEssay = d.isEssay || false;
                    let status = '';
                    let statusColor = '';
                    let statusIcon = '';
                    
                    if (isEssay) {
                      if (d.isCorrect === null && !isFullyGraded) {
                        status = 'در انتظار تصحیح';
                        statusColor = '#f59e0b';
                        statusIcon = '⏳';
                      } else if (d.isCorrect === true) {
                        status = 'صحیح';
                        statusColor = '#34d399';
                        statusIcon = '✅';
                      } else if (d.isCorrect === false) {
                        status = 'غلط';
                        statusColor = '#ef4444';
                        statusIcon = '❌';
                      } else {
                        status = 'نیاز به تصحیح';
                        statusColor = '#f59e0b';
                        statusIcon = '⏳';
                      }
                    } else {
                      if (d.isCorrect === true) {
                        status = 'صحیح';
                        statusColor = '#34d399';
                        statusIcon = '✅';
                      } else {
                        status = 'غلط';
                        statusColor = '#ef4444';
                        statusIcon = '❌';
                      }
                    }

                    const scoreDisplay = isEssay && !isFullyGraded ? '...' : d.score;

                    return (
                      <tr key={idx}>
                        <td className="take-exam resultDetailsQNumber">{idx + 1}</td>
                        <td className="take-exam resultDetailsQType">
                          {isEssay ? '📝 تشریحی' : '🎯 تستی'}
                        </td>
                        <td className="take-exam resultDetailsQStatus" style={{ color: statusColor }}>
                          {statusIcon} {status}
                        </td>
                        <td className="take-exam resultDetailsQScore">{scoreDisplay}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>


          {!currentStudentId && (
  <div className="take-exam confirmWarning confirmGuestWarning" style={{ marginTop: 14 }}>
    <span className="take-exam confirmWarningIcon">👤</span>
    <span>این نتیجه به‌صورت <strong>میهمان</strong> ذخیره شد. برای ذخیره دائمی، وارد حساب خود شوید.</span>
  </div>
)}

          <div className="take-exam resultActions">
            <button className="take-exam resultBtn resultBtnPrimary" onClick={closeResult}>
              ✕ بستن کارنامه
            </button>
            {/*<button className="take-exam resultBtn resultBtnOutline" onClick={() => window.print()}>
              🖨️ چاپ کارنامه
            </button>*/}
            {canReattempt && (
              <button
                className="take-exam resultBtn resultBtnSuccess"
                onClick={() => {
                  setAnswers({});
                  setBookmarked([]);
                  setCurrentStep(0);
                  setIsSubmitted(false);
                  setResult(null);
                  setShowConfirm(false);
                  setShowResultModal(false);
                  setExamStarted(false);
                  setTimeLeft((toNumber(examData.duration) || 0) * 60);
                  localStorage.removeItem(`exam_${examData.id}_answers`);
                  localStorage.removeItem(`exam_${examData.id}_time`);
                  localStorage.removeItem(`exam_${examData.id}_step`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => setExamStarted(true), 100);
                }}
              >
                🔄 شرکت مجدد ({maxAttempts - previousAttempts - 1} بار دیگر)
              </button>
            )}
            {isGlobal && previousAttempts >= maxAttempts - 1 && (
              <span className="take-exam resultLimitReached">⛔ محدودیت شرکت به پایان رسید</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== تایید ارسال =====
  if (showConfirm) {
    const hasEssay = shuffledQuestions.some(q => q.type === 'essay' || q.type === 'file');
    const totalQuestions = shuffledQuestions.length;
    const answeredCount = getAnsweredCount();
    const missingCount = totalQuestions - answeredCount;

    return (
      <div className={`take-exam confirmOverlay ${isDark ? 'dark-mode' : ''}`}>
        <div className="take-exam confirmCard">
          <div className="take-exam confirmHeader">
            <span className="take-exam confirmIcon">⚠️</span>
            <h3 className="take-exam confirmTitle">تایید ارسال</h3>
          </div>

          <p className="take-exam confirmMessage">
            آیا از ارسال پاسخ‌های خود مطمئن هستید؟
          </p>

          <div className="take-exam confirmStats">
            <div className="take-exam confirmStatItem">
              <span className="take-exam confirmStatNumber">{answeredCount}</span>
              <span className="take-exam confirmStatLabel">پاسخ داده شده</span>
            </div>
            <div className="take-exam confirmStatDivider">/</div>
            <div className="take-exam confirmStatItem">
              <span className="take-exam confirmStatNumber">{totalQuestions}</span>
              <span className="take-exam confirmStatLabel">کل سوالات</span>
            </div>
            {missingCount > 0 && (
              <div className="take-exam confirmStatItem confirmStatWarning">
                <span className="take-exam confirmStatNumber">{missingCount}</span>
                <span className="take-exam confirmStatLabel">پاسخ داده نشده</span>
              </div>
            )}
          </div>

          {hasEssay && (
            <div className="take-exam confirmWarning confirmEssayWarning">
              <span className="take-exam confirmWarningIcon">📝</span>
              <span>این آزمون دارای سوالات تشریحی است که پس از تصحیح معلم، نمره نهایی اعلام می‌شود.</span>
            </div>
          )}

          {isGlobal && previousAttempts > 0 && (
            <div className="take-exam confirmWarning confirmReattemptWarning">
              <span className="take-exam confirmWarningIcon">🔄</span>
              <span>این {previousAttempts + 1}مین تلاش شماست. پس از ارسال، پاسخ‌های قبلی با پاسخ‌های جدید جایگزین می‌شوند.</span>
            </div>
          )}

          {!currentStudentId && (
            <div className="take-exam confirmWarning confirmGuestWarning">
              <span className="take-exam confirmWarningIcon">👤</span>
              <span>شما به عنوان میهمان وارد شده‌اید. نتایج شما بدون اتصال به حساب کاربری ذخیره می‌شود.</span>
            </div>
          )}

          <div className="take-exam confirmActions">
            <button 
              className="take-exam confirmBtn confirmBtnSecondary" 
              onClick={() => setShowConfirm(false)}
            >
              ← بازگشت
            </button>
            <button 
              className="take-exam confirmBtn confirmBtnPrimary" 
              onClick={confirmSubmit} 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="take-exam confirmBtnSpinner">⏳</span>
                  در حال ارسال...
                </>
              ) : (
                '✅ بله، ارسال کن'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== صفحه اصلی =====
  const currentQ = shuffledQuestions[currentStep];

  if (!currentQ) {
    return (
      <div className={`take-exam ${isDark ? 'dark-mode' : ''}`}>
        <div className={`take-exam error ${isDark ? 'dark-mode' : ''}`}>سوالی وجود ندارد</div>
      </div>
    );
  }

  const displayMode = examData.displayMode || 'step';
  const isAllMode = displayMode === 'all';

  return (
    <div className={`take-exam ${isDark ? 'dark-mode' : ''}`}>
      {renderStartScreen()}

      {/* ===== مودال هشدار خروج از آزمون ===== */}
{showLeaveWarning && (
  <div className={`take-exam confirmOverlay ${isDark ? 'dark-mode' : ''}`}>
    <div className="take-exam confirmCard">
      <div className="take-exam confirmHeader">
        <span className="take-exam confirmIcon">⚠️</span>
        <h3 className="take-exam confirmTitle">هشدار خروج از آزمون</h3>
      </div>

      <p className="take-exam confirmMessage">
        اگر از این صفحه خارج شوید، آزمون شما <strong>پایان می‌یابد</strong> و پاسخ‌های فعلی به عنوان پاسخ نهایی ثبت می‌شوند.
      </p>

      <div className="take-exam confirmWarning confirmEssayWarning">
        <span className="take-exam confirmWarningIcon">⏱</span>
        <span>زمان باقی‌مانده: <strong>{formatTime(timeLeft)}</strong></span>
      </div>

      <div className="take-exam confirmActions">
        <button
          className="take-exam confirmBtn confirmBtnSecondary"
          onClick={() => setShowLeaveWarning(false)}
        >
          ← ماندن در آزمون
        </button>
        <button
          className="take-exam confirmBtn confirmBtnPrimary"
          onClick={() => {
            setShowLeaveWarning(false);
            submitExam();
          }}
        >
          ✅ خروج و اتمام آزمون
        </button>
      </div>
    </div>
  </div>
)}

      {examStarted && (
        <>
          <header className="take-exam header">
            <div className="take-exam headerLeft">
              {!embeddedMode && (
  <button
    className="take-exam exitBtn"
    onClick={() => setShowLeaveWarning(true)}
    title="خروج"
  >
    ✕
  </button>
)}
              <span className="take-exam examTitle">{examData.title}</span>
              {isGlobal && <span className="take-exam globalLabel">🌍 عمومی</span>}
              <span className="take-exam examSub">{examData.book}</span>
              {isGlobal && examData.grade && (
                <span className="take-exam examMeta">
                  {/* 🔽 CHANGED: نمایش رشته */}
                  {formatGradeField(examData.grade, examData.field)}
                  {examData.subject ? ` - ${examData.subject}` : ''}
                  {examData.chapter ? ` - فصل ${examData.chapter}` : ''}
                </span>
              )}
              {isGlobal && previousAttempts > 0 && (
                <span className="take-exam attemptLabel">تلاش {previousAttempts + 1}</span>
              )}
            </div>
            <div className="take-exam headerRight">
              <button className="take-exam fontBtn" onClick={() => setFontSize(Math.max(12, fontSize - 2))}>A-</button>
              <span className="take-exam fontSizeDisplay">{fontSize}px</span>
              <button className="take-exam fontBtn" onClick={() => setFontSize(Math.min(28, fontSize + 2))}>A+</button>
              <button className="take-exam fullscreenBtn" onClick={toggleFullScreen}>
                {isFullScreen ? '⛶ کوچک' : '⛶ تمام صفحه'}
              </button>
              <div className="take-exam timer">
                <span className="take-exam timerIcon">⏱</span>
                <span className={timeLeft < 60 ? 'timerWarning' : ''}>
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          </header>

          {showCalculator && (
            <div className="take-exam calculator">
              <div className="take-exam calculatorDisplay">
                <span className="take-exam calculatorInput">{calculatorInput}</span>
                <span className="take-exam calculatorResult">{calculatorResult}</span>
              </div>
              <div className="take-exam calculatorButtons">
                {['7','8','9','/','4','5','6','*','1','2','3','-','0','.','C','+','='].map((btn) => (
                  <button
                    key={btn}
                    className={`take-exam calcBtn ${btn === '=' ? 'calcEqual' : ''} ${btn === 'C' ? 'calcClear' : ''}`}
                    onClick={() => handleCalculator(btn)}
                  >
                    {btn}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="take-exam progressBar">
            <div className="take-exam progressFill" style={{ width: `${(getAnsweredCount() / shuffledQuestions.length) * 100}%` }} />
            <span className="take-exam progressText">{getAnsweredCount()} / {shuffledQuestions.length}</span>
          </div>

          <div className="take-exam mainContent">
            <aside className="take-exam navPanel">
              <h4>📋 سوالات</h4>
              <div className="take-exam navFilter">
                <button className={`take-exam filterBtn ${navFilter === 'all' ? 'active' : ''}`} onClick={() => setNavFilter('all')}>همه</button>
                <button className={`take-exam filterBtn ${navFilter === 'answered' ? 'active' : ''}`} onClick={() => setNavFilter('answered')}>پاسخ داده</button>
                <button className={`take-exam filterBtn ${navFilter === 'bookmarked' ? 'active' : ''}`} onClick={() => setNavFilter('bookmarked')}>نشانه</button>
              </div>
              <div className="take-exam navGrid">
                {shuffledQuestions.map((q, idx) => {
                  const isAnswered_ = isAnswered(idx);
                  const isBookmarked_ = bookmarked.includes(idx);
                  const isActive = currentStep === idx;
                  const isEssay = q.type === 'essay' || q.type === 'file';
                  const isRequired = q.required && !isAnswered_;

                  if (navFilter === 'answered' && !isAnswered_) return null;
                  if (navFilter === 'bookmarked' && !isBookmarked_) return null;

                  return (
                    <button
                      key={idx}
                      className={`take-exam navBtn ${isActive ? 'active' : ''} ${
                        isAnswered_ ? 'answered' : 'unanswered'
                      } ${isBookmarked_ ? 'bookmarked' : ''} ${isRequired ? 'requiredNav' : ''}`}
                      onClick={() => goToQuestion(idx)}
                      title={isEssay ? 'سوال تشریحی' : ''}
                    >
                      {idx + 1}
                      {isEssay && <span className="take-exam navEssayDot"></span>}
                      {isRequired && <span className="take-exam navRequiredDot"></span>}
                    </button>
                  );
                })}
              </div>
            </aside>

            <main className="take-exam questionArea">
              {isPaused ? (
                <div className="take-exam pauseOverlay">
                  <h2>⏸️ آزمون متوقف شد</h2>
                  <p>برای ادامه روی دکمه ▶️ ادامه کلیک کنید</p>
                </div>
              ) : (
                <>
                  {isAllMode ? (
                    <div className="take-exam allQuestionsWrapper">
                      {renderAllQuestions()}
                    </div>
                  ) : (
                    currentQ && renderQuestion(currentQ, currentStep)
                  )}
                </>
              )}
            </main>
          </div>

          <footer className="take-exam navigation">
            {!isAllMode && (
              <button className="take-exam btn btnOutline" onClick={prevQuestion} disabled={currentStep === 0}>
                ← قبلی
              </button>
            )}
            <div className="take-exam navCenter">
              {!isAllMode && (
                <span className="take-exam stepIndicator">{currentStep + 1} از {shuffledQuestions.length}</span>
              )}
              <button className="take-exam btn btnSuccess" onClick={handleSubmit}>
                📤 ارسال آزمون
              </button>
            </div>
            {!isAllMode && (
              <button className="take-exam btn btnOutline" onClick={nextQuestion} disabled={currentStep === shuffledQuestions.length - 1}>
                بعدی →
              </button>
            )}
          </footer>
        </>
      )}
    </div>
  );
};

export default TakeExam;